/**
 * theme.js — Design-token system for Kosteczki.
 *
 * The app now supports multiple full-screen colour themes. Keep all reusable
 * colour, spacing and shadow tokens here so screens can switch palettes via a
 * single `themeKey` user setting.
 */

export const DEFAULT_THEME_KEY = 'nebula';

const sharedShadows = {
  card: '0 20px 60px rgba(0,0,0,0.30)',
  modal: '0 20px 60px rgba(0,0,0,0.50)',
  input: '0 1px 2px 0 rgba(0,0,0,0.05)',
};

export const radii = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  pill: '9999px',
};

export const space = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
};

export const zIndex = {
  base: 0,
  raised: 10,
  dropdown: 100,
  modal: 1000,
  toast: 2000,
  top: 9999,
};

export const typography = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSizeBase: '16px',
  fontSizeSm: '14px',
  fontSizeXs: '12px',
  fontWeightNormal: '400',
  fontWeightMedium: '500',
  fontWeightBold: '600',
};

const themeLibrary = {
  nebula: {
    id: 'nebula',
    labelKey: 'themeNebula',
    descriptionKey: 'themeNebulaDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #101828 0%, #312e81 48%, #0f4c81 100%)',
      accent: '#8b5cf6',
      accentAlt: '#38bdf8',
    },
    palette: {
      primary: '#8b5cf6',
      primarySoft: '#c4b5fd',
      secondary: '#38bdf8',
      secondarySoft: '#bae6fd',
      backgroundStart: '#111827',
      backgroundMid: '#312e81',
      backgroundEnd: '#0f4c81',
      heroStart: '#0b1120',
      heroMid: '#1d2560',
      heroEnd: '#114364',
      modalStart: '#121c30',
      modalEnd: '#070d18',
      boardStart: '#162338',
      boardEnd: '#09121f',
      textBody: '#eff6ff',
      textMuted: '#93c5fd',
      textDisabled: '#7f8ea8',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(15, 23, 42, 0.46)',
      surface2: 'rgba(99, 102, 241, 0.12)',
      surface3: 'rgba(147, 197, 253, 0.14)',
      card: 'rgba(10, 15, 28, 0.58)',
      overlay: 'rgba(3, 7, 18, 0.82)',
    },
    borders: {
      subtle: 'rgba(255,255,255,0.07)',
      default: 'rgba(148,163,184,0.18)',
      glass: 'rgba(148,163,184,0.22)',
      accent: 'rgba(139,92,246,0.30)',
      active: 'rgba(139,92,246,0.46)',
    },
    accents: {
      primary: '#8b5cf6',
      secondary: '#38bdf8',
      soft: 'rgba(99,102,241,0.24)',
      softBorder: 'rgba(129,140,248,0.38)',
      info: '#93c5fd',
      infoSoft: 'rgba(56,189,248,0.14)',
      infoBorder: 'rgba(56,189,248,0.30)',
      success: '#34d399',
      successSoft: 'rgba(16,185,129,0.14)',
      successBorder: 'rgba(16,185,129,0.32)',
      warning: '#fbbf24',
      warningSoft: 'rgba(245,158,11,0.14)',
      warningBorder: 'rgba(245,158,11,0.32)',
      danger: '#f87171',
      dangerSoft: 'rgba(239,68,68,0.14)',
      dangerBorder: 'rgba(239,68,68,0.32)',
      neutralSoft: 'rgba(71,85,105,0.46)',
      neutralBorder: 'rgba(148,163,184,0.24)',
    },
  },
  ember: {
    id: 'ember',
    labelKey: 'themeEmber',
    descriptionKey: 'themeEmberDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #2a1612 0%, #6d3f1f 48%, #c26a2f 100%)',
      accent: '#fb923c',
      accentAlt: '#fca5a5',
    },
    palette: {
      primary: '#fb923c',
      primarySoft: '#fed7aa',
      secondary: '#f97316',
      secondarySoft: '#fdba74',
      backgroundStart: '#221411',
      backgroundMid: '#6b3d20',
      backgroundEnd: '#c46a2a',
      heroStart: '#180f0d',
      heroMid: '#412418',
      heroEnd: '#8a4b23',
      modalStart: '#2a1813',
      modalEnd: '#17110f',
      boardStart: '#342019',
      boardEnd: '#191414',
      textBody: '#fff4eb',
      textMuted: '#fdba74',
      textDisabled: '#ad8a79',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(37, 21, 16, 0.44)',
      surface2: 'rgba(249,115,22,0.12)',
      surface3: 'rgba(251,146,60,0.15)',
      card: 'rgba(27, 17, 13, 0.60)',
      overlay: 'rgba(15, 9, 7, 0.84)',
    },
    borders: {
      subtle: 'rgba(255,237,213,0.08)',
      default: 'rgba(251,191,36,0.16)',
      glass: 'rgba(251,191,36,0.20)',
      accent: 'rgba(249,115,22,0.34)',
      active: 'rgba(251,146,60,0.48)',
    },
    accents: {
      primary: '#fb923c',
      secondary: '#f97316',
      soft: 'rgba(249,115,22,0.22)',
      softBorder: 'rgba(251,146,60,0.36)',
      info: '#fdba74',
      infoSoft: 'rgba(251,146,60,0.12)',
      infoBorder: 'rgba(251,146,60,0.24)',
      success: '#4ade80',
      successSoft: 'rgba(74,222,128,0.12)',
      successBorder: 'rgba(74,222,128,0.26)',
      warning: '#facc15',
      warningSoft: 'rgba(250,204,21,0.15)',
      warningBorder: 'rgba(250,204,21,0.28)',
      danger: '#f87171',
      dangerSoft: 'rgba(248,113,113,0.14)',
      dangerBorder: 'rgba(248,113,113,0.30)',
      neutralSoft: 'rgba(120, 53, 15, 0.54)',
      neutralBorder: 'rgba(251,191,36,0.22)',
    },
  },
  lagoon: {
    id: 'lagoon',
    labelKey: 'themeLagoon',
    descriptionKey: 'themeLagoonDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #102a43 0%, #155e75 48%, #0f766e 100%)',
      accent: '#2dd4bf',
      accentAlt: '#7dd3fc',
    },
    palette: {
      primary: '#2dd4bf',
      primarySoft: '#99f6e4',
      secondary: '#38bdf8',
      secondarySoft: '#bae6fd',
      backgroundStart: '#0f2f3d',
      backgroundMid: '#155e75',
      backgroundEnd: '#1d4f74',
      heroStart: '#081b25',
      heroMid: '#103448',
      heroEnd: '#13556b',
      modalStart: '#102532',
      modalEnd: '#09131f',
      boardStart: '#123040',
      boardEnd: '#091725',
      textBody: '#e0fbff',
      textMuted: '#7dd3fc',
      textDisabled: '#6d90a1',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(10, 35, 47, 0.42)',
      surface2: 'rgba(45,212,191,0.10)',
      surface3: 'rgba(56,189,248,0.12)',
      card: 'rgba(9, 28, 38, 0.58)',
      overlay: 'rgba(4, 13, 20, 0.84)',
    },
    borders: {
      subtle: 'rgba(186,230,253,0.08)',
      default: 'rgba(125,211,252,0.16)',
      glass: 'rgba(125,211,252,0.20)',
      accent: 'rgba(45,212,191,0.32)',
      active: 'rgba(45,212,191,0.46)',
    },
    accents: {
      primary: '#2dd4bf',
      secondary: '#38bdf8',
      soft: 'rgba(45,212,191,0.20)',
      softBorder: 'rgba(56,189,248,0.34)',
      info: '#7dd3fc',
      infoSoft: 'rgba(56,189,248,0.12)',
      infoBorder: 'rgba(56,189,248,0.24)',
      success: '#5eead4',
      successSoft: 'rgba(45,212,191,0.12)',
      successBorder: 'rgba(45,212,191,0.24)',
      warning: '#facc15',
      warningSoft: 'rgba(250,204,21,0.14)',
      warningBorder: 'rgba(250,204,21,0.28)',
      danger: '#fb7185',
      dangerSoft: 'rgba(251,113,133,0.14)',
      dangerBorder: 'rgba(251,113,133,0.30)',
      neutralSoft: 'rgba(30, 64, 82, 0.52)',
      neutralBorder: 'rgba(125,211,252,0.22)',
    },
  },
  forest: {
    id: 'forest',
    labelKey: 'themeForest',
    descriptionKey: 'themeForestDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #0f1f17 0%, #1f4d3a 52%, #4d7c0f 100%)',
      accent: '#34d399',
      accentAlt: '#84cc16',
    },
    palette: {
      primary: '#34d399',
      primarySoft: '#a7f3d0',
      secondary: '#84cc16',
      secondarySoft: '#d9f99d',
      backgroundStart: '#101b15',
      backgroundMid: '#1f4d3a',
      backgroundEnd: '#365314',
      heroStart: '#09120d',
      heroMid: '#143124',
      heroEnd: '#28441d',
      modalStart: '#12211a',
      modalEnd: '#0c1511',
      boardStart: '#15261e',
      boardEnd: '#0c1511',
      textBody: '#ecfdf5',
      textMuted: '#a7f3d0',
      textDisabled: '#7b9486',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(12, 30, 22, 0.44)',
      surface2: 'rgba(52,211,153,0.10)',
      surface3: 'rgba(132,204,22,0.12)',
      card: 'rgba(11, 24, 18, 0.60)',
      overlay: 'rgba(6, 13, 9, 0.84)',
    },
    borders: {
      subtle: 'rgba(220,252,231,0.08)',
      default: 'rgba(134,239,172,0.16)',
      glass: 'rgba(134,239,172,0.20)',
      accent: 'rgba(52,211,153,0.30)',
      active: 'rgba(52,211,153,0.42)',
    },
    accents: {
      primary: '#34d399',
      secondary: '#84cc16',
      soft: 'rgba(52,211,153,0.18)',
      softBorder: 'rgba(52,211,153,0.30)',
      info: '#a7f3d0',
      infoSoft: 'rgba(52,211,153,0.12)',
      infoBorder: 'rgba(52,211,153,0.22)',
      success: '#34d399',
      successSoft: 'rgba(52,211,153,0.12)',
      successBorder: 'rgba(52,211,153,0.24)',
      warning: '#facc15',
      warningSoft: 'rgba(250,204,21,0.14)',
      warningBorder: 'rgba(250,204,21,0.28)',
      danger: '#fb7185',
      dangerSoft: 'rgba(251,113,133,0.14)',
      dangerBorder: 'rgba(251,113,133,0.30)',
      neutralSoft: 'rgba(28, 48, 38, 0.54)',
      neutralBorder: 'rgba(134,239,172,0.22)',
    },
  },
  aurora: {
    id: 'aurora',
    labelKey: 'themeAurora',
    descriptionKey: 'themeAuroraDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #07171d 0%, #0f766e 50%, #84cc16 100%)',
      accent: '#2dd4bf',
      accentAlt: '#bef264',
    },
    palette: {
      primary: '#2dd4bf',
      primarySoft: '#99f6e4',
      secondary: '#bef264',
      secondarySoft: '#d9f99d',
      backgroundStart: '#07161c',
      backgroundMid: '#0f5f61',
      backgroundEnd: '#365314',
      heroStart: '#050e12',
      heroMid: '#123b3d',
      heroEnd: '#2d5a1c',
      modalStart: '#0d2025',
      modalEnd: '#060d10',
      boardStart: '#10282f',
      boardEnd: '#071117',
      textBody: '#ecfeff',
      textMuted: '#99f6e4',
      textDisabled: '#7b98a0',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(7, 25, 30, 0.52)',
      surface2: 'rgba(45,212,191,0.12)',
      surface3: 'rgba(190,242,100,0.14)',
      card: 'rgba(5, 18, 22, 0.68)',
      overlay: 'rgba(2, 7, 9, 0.86)',
    },
    borders: {
      subtle: 'rgba(236,254,255,0.08)',
      default: 'rgba(153,246,228,0.18)',
      glass: 'rgba(153,246,228,0.22)',
      accent: 'rgba(45,212,191,0.32)',
      active: 'rgba(45,212,191,0.44)',
    },
    accents: {
      primary: '#2dd4bf',
      secondary: '#bef264',
      soft: 'rgba(45,212,191,0.20)',
      softBorder: 'rgba(45,212,191,0.34)',
      info: '#67e8f9',
      infoSoft: 'rgba(103,232,249,0.12)',
      infoBorder: 'rgba(103,232,249,0.24)',
      success: '#bef264',
      successSoft: 'rgba(190,242,100,0.14)',
      successBorder: 'rgba(190,242,100,0.28)',
      warning: '#facc15',
      warningSoft: 'rgba(250,204,21,0.14)',
      warningBorder: 'rgba(250,204,21,0.28)',
      danger: '#fb7185',
      dangerSoft: 'rgba(251,113,133,0.14)',
      dangerBorder: 'rgba(251,113,133,0.30)',
      neutralSoft: 'rgba(18, 39, 35, 0.66)',
      neutralBorder: 'rgba(190,242,100,0.24)',
    },
  },
  sunset: {
    id: 'sunset',
    labelKey: 'themeSunset',
    descriptionKey: 'themeSunsetDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #211021 0%, #8b2f5f 50%, #f59e0b 100%)',
      accent: '#fb7185',
      accentAlt: '#fbbf24',
    },
    palette: {
      primary: '#fb7185',
      primarySoft: '#fecdd3',
      secondary: '#fbbf24',
      secondarySoft: '#fde68a',
      backgroundStart: '#1d1020',
      backgroundMid: '#6d2146',
      backgroundEnd: '#b45309',
      heroStart: '#120915',
      heroMid: '#43203b',
      heroEnd: '#8a3b1b',
      modalStart: '#22111d',
      modalEnd: '#10070d',
      boardStart: '#2b151f',
      boardEnd: '#12080c',
      textBody: '#fff7f5',
      textMuted: '#fbcfe8',
      textDisabled: '#c09196',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(65, 24, 47, 0.52)',
      surface2: 'rgba(251,113,133,0.14)',
      surface3: 'rgba(251,191,36,0.16)',
      card: 'rgba(24, 10, 20, 0.72)',
      overlay: 'rgba(10, 3, 7, 0.88)',
    },
    borders: {
      subtle: 'rgba(255,241,242,0.08)',
      default: 'rgba(251,191,36,0.18)',
      glass: 'rgba(251,191,36,0.22)',
      accent: 'rgba(251,113,133,0.32)',
      active: 'rgba(251,113,133,0.44)',
    },
    accents: {
      primary: '#fb7185',
      secondary: '#fbbf24',
      soft: 'rgba(251,113,133,0.18)',
      softBorder: 'rgba(251,113,133,0.32)',
      info: '#fdba74',
      infoSoft: 'rgba(253,186,116,0.12)',
      infoBorder: 'rgba(253,186,116,0.24)',
      success: '#fed7aa',
      successSoft: 'rgba(254,215,170,0.14)',
      successBorder: 'rgba(254,215,170,0.26)',
      warning: '#fbbf24',
      warningSoft: 'rgba(251,191,36,0.14)',
      warningBorder: 'rgba(251,191,36,0.28)',
      danger: '#e11d48',
      dangerSoft: 'rgba(225,29,72,0.16)',
      dangerBorder: 'rgba(225,29,72,0.30)',
      neutralSoft: 'rgba(67, 28, 42, 0.68)',
      neutralBorder: 'rgba(251,191,36,0.24)',
    },
  },
  citrus: {
    id: 'citrus',
    labelKey: 'themeCitrus',
    descriptionKey: 'themeCitrusDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #12150d 0%, #365314 50%, #f97316 100%)',
      accent: '#a3e635',
      accentAlt: '#fb923c',
    },
    palette: {
      primary: '#a3e635',
      primarySoft: '#d9f99d',
      secondary: '#fb923c',
      secondarySoft: '#fed7aa',
      backgroundStart: '#12150d',
      backgroundMid: '#365314',
      backgroundEnd: '#b45309',
      heroStart: '#0b0e08',
      heroMid: '#1f2f0f',
      heroEnd: '#6c350f',
      modalStart: '#17180d',
      modalEnd: '#0b0b08',
      boardStart: '#1f2410',
      boardEnd: '#0c0d08',
      textBody: '#fffdf4',
      textMuted: '#d9f99d',
      textDisabled: '#b0a382',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(26, 33, 13, 0.52)',
      surface2: 'rgba(163,230,53,0.12)',
      surface3: 'rgba(249,115,22,0.15)',
      card: 'rgba(17, 18, 10, 0.74)',
      overlay: 'rgba(9, 8, 3, 0.88)',
    },
    borders: {
      subtle: 'rgba(254,252,232,0.08)',
      default: 'rgba(190,242,100,0.18)',
      glass: 'rgba(190,242,100,0.22)',
      accent: 'rgba(163,230,53,0.30)',
      active: 'rgba(249,115,22,0.40)',
    },
    accents: {
      primary: '#a3e635',
      secondary: '#fb923c',
      soft: 'rgba(163,230,53,0.18)',
      softBorder: 'rgba(163,230,53,0.32)',
      info: '#fde68a',
      infoSoft: 'rgba(253,230,138,0.12)',
      infoBorder: 'rgba(253,230,138,0.24)',
      success: '#bef264',
      successSoft: 'rgba(190,242,100,0.14)',
      successBorder: 'rgba(190,242,100,0.26)',
      warning: '#facc15',
      warningSoft: 'rgba(250,204,21,0.14)',
      warningBorder: 'rgba(250,204,21,0.28)',
      danger: '#fb7185',
      dangerSoft: 'rgba(251,113,133,0.14)',
      dangerBorder: 'rgba(251,113,133,0.30)',
      neutralSoft: 'rgba(31, 36, 16, 0.72)',
      neutralBorder: 'rgba(190,242,100,0.24)',
    },
  },
  laser: {
    id: 'laser',
    labelKey: 'themeLaser',
    descriptionKey: 'themeLaserDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #0a1020 0%, #1d4ed8 50%, #f43f5e 100%)',
      accent: '#38bdf8',
      accentAlt: '#fb7185',
    },
    palette: {
      primary: '#38bdf8',
      primarySoft: '#bae6fd',
      secondary: '#fb7185',
      secondarySoft: '#fecdd3',
      backgroundStart: '#09101d',
      backgroundMid: '#1d4ed8',
      backgroundEnd: '#9f1239',
      heroStart: '#050913',
      heroMid: '#162f7a',
      heroEnd: '#6f1739',
      modalStart: '#11182a',
      modalEnd: '#060a12',
      boardStart: '#121b31',
      boardEnd: '#080c16',
      textBody: '#f4fbff',
      textMuted: '#bfdbfe',
      textDisabled: '#96a7c4',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(17, 24, 42, 0.56)',
      surface2: 'rgba(56,189,248,0.14)',
      surface3: 'rgba(244,63,94,0.14)',
      card: 'rgba(10, 14, 25, 0.72)',
      overlay: 'rgba(4, 6, 11, 0.88)',
    },
    borders: {
      subtle: 'rgba(244,251,255,0.08)',
      default: 'rgba(96,165,250,0.18)',
      glass: 'rgba(96,165,250,0.22)',
      accent: 'rgba(56,189,248,0.30)',
      active: 'rgba(251,113,133,0.42)',
    },
    accents: {
      primary: '#38bdf8',
      secondary: '#fb7185',
      soft: 'rgba(56,189,248,0.18)',
      softBorder: 'rgba(56,189,248,0.32)',
      info: '#bfdbfe',
      infoSoft: 'rgba(191,219,254,0.12)',
      infoBorder: 'rgba(191,219,254,0.24)',
      success: '#22d3ee',
      successSoft: 'rgba(34,211,238,0.14)',
      successBorder: 'rgba(34,211,238,0.26)',
      warning: '#fbbf24',
      warningSoft: 'rgba(251,191,36,0.14)',
      warningBorder: 'rgba(251,191,36,0.28)',
      danger: '#fb7185',
      dangerSoft: 'rgba(251,113,133,0.16)',
      dangerBorder: 'rgba(251,113,133,0.30)',
      neutralSoft: 'rgba(28, 39, 68, 0.72)',
      neutralBorder: 'rgba(96,165,250,0.24)',
    },
  },
  graphite: {
    id: 'graphite',
    labelKey: 'themeGraphite',
    descriptionKey: 'themeGraphiteDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #050505 0%, #161616 52%, #3a3a3a 100%)',
      accent: '#e5e7eb',
      accentAlt: '#a3a3a3',
    },
    palette: {
      primary: '#e5e7eb',
      primarySoft: '#f5f5f5',
      secondary: '#a3a3a3',
      secondarySoft: '#d4d4d8',
      backgroundStart: '#050505',
      backgroundMid: '#121212',
      backgroundEnd: '#2f2f2f',
      heroStart: '#030303',
      heroMid: '#0d0d0d',
      heroEnd: '#232323',
      modalStart: '#121212',
      modalEnd: '#060606',
      boardStart: '#171717',
      boardEnd: '#080808',
      textBody: '#fafafa',
      textMuted: '#b4b4b8',
      textDisabled: '#737373',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.02)',
      surface1: 'rgba(24,24,27,0.68)',
      surface2: 'rgba(255,255,255,0.08)',
      surface3: 'rgba(255,255,255,0.12)',
      card: 'rgba(8,8,8,0.78)',
      overlay: 'rgba(0,0,0,0.88)',
    },
    borders: {
      subtle: 'rgba(255,255,255,0.06)',
      default: 'rgba(255,255,255,0.12)',
      glass: 'rgba(255,255,255,0.16)',
      accent: 'rgba(229,231,235,0.22)',
      active: 'rgba(229,231,235,0.34)',
    },
    accents: {
      primary: '#e5e7eb',
      secondary: '#a3a3a3',
      soft: 'rgba(229,231,235,0.10)',
      softBorder: 'rgba(229,231,235,0.18)',
      info: '#d4d4d8',
      infoSoft: 'rgba(212,212,216,0.10)',
      infoBorder: 'rgba(212,212,216,0.18)',
      success: '#f5f5f5',
      successSoft: 'rgba(245,245,245,0.12)',
      successBorder: 'rgba(245,245,245,0.20)',
      warning: '#a3a3a3',
      warningSoft: 'rgba(163,163,163,0.12)',
      warningBorder: 'rgba(163,163,163,0.20)',
      danger: '#737373',
      dangerSoft: 'rgba(115,115,115,0.14)',
      dangerBorder: 'rgba(115,115,115,0.24)',
      neutralSoft: 'rgba(23,23,23,0.84)',
      neutralBorder: 'rgba(255,255,255,0.14)',
    },
  },
  rose: {
    id: 'rose',
    labelKey: 'themeRose',
    descriptionKey: 'themeRoseDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #1d0d16 0%, #5f2540 52%, #cf6f98 100%)',
      accent: '#ff8fc7',
      accentAlt: '#ffe4ef',
    },
    palette: {
      primary: '#ff8fc7',
      primarySoft: '#ffe4ef',
      secondary: '#ff6fa6',
      secondarySoft: '#ffd2e4',
      backgroundStart: '#1b0d14',
      backgroundMid: '#472036',
      backgroundEnd: '#9f4b73',
      heroStart: '#12080d',
      heroMid: '#2b1321',
      heroEnd: '#6d3350',
      modalStart: '#1a0d14',
      modalEnd: '#0f070b',
      boardStart: '#24111a',
      boardEnd: '#0e0609',
      textBody: '#fff8fb',
      textMuted: '#f7c2da',
      textDisabled: '#bf8da4',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.035)',
      surface1: 'rgba(80, 29, 56, 0.52)',
      surface2: 'rgba(255,143,199,0.14)',
      surface3: 'rgba(255,111,166,0.16)',
      card: 'rgba(27, 10, 20, 0.74)',
      overlay: 'rgba(9, 3, 6, 0.88)',
    },
    borders: {
      subtle: 'rgba(255,228,239,0.08)',
      default: 'rgba(255,196,221,0.18)',
      glass: 'rgba(255,196,221,0.24)',
      accent: 'rgba(255,143,199,0.34)',
      active: 'rgba(255,143,199,0.46)',
    },
    accents: {
      primary: '#ff8fc7',
      secondary: '#ff6fa6',
      soft: 'rgba(255,143,199,0.18)',
      softBorder: 'rgba(255,143,199,0.32)',
      info: '#ffd7e7',
      infoSoft: 'rgba(255,215,231,0.12)',
      infoBorder: 'rgba(255,215,231,0.24)',
      success: '#ffc3de',
      successSoft: 'rgba(255,195,222,0.12)',
      successBorder: 'rgba(255,195,222,0.24)',
      warning: '#ff9fbe',
      warningSoft: 'rgba(255,159,190,0.14)',
      warningBorder: 'rgba(255,159,190,0.28)',
      danger: '#d6336c',
      dangerSoft: 'rgba(214,51,108,0.16)',
      dangerBorder: 'rgba(214,51,108,0.30)',
      neutralSoft: 'rgba(72, 25, 50, 0.68)',
      neutralBorder: 'rgba(255,195,222,0.24)',
    },
  },
  dawn: {
    id: 'dawn',
    labelKey: 'themeDawn',
    descriptionKey: 'themeDawnDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #1b1f2b 0%, #687799 52%, #d8a07f 100%)',
      accent: '#c4b5fd',
      accentAlt: '#fdba74',
    },
    palette: {
      primary: '#c4b5fd',
      primarySoft: '#ede9fe',
      secondary: '#d38f6c',
      secondarySoft: '#fed7aa',
      backgroundStart: '#181c25',
      backgroundMid: '#5f6f8e',
      backgroundEnd: '#9c735f',
      heroStart: '#11141a',
      heroMid: '#39475c',
      heroEnd: '#735543',
      modalStart: '#1a1d26',
      modalEnd: '#0e1015',
      boardStart: '#202531',
      boardEnd: '#101319',
      textBody: '#fffaf6',
      textMuted: '#e9ddff',
      textDisabled: '#a59daf',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(29, 34, 45, 0.54)',
      surface2: 'rgba(196,181,253,0.12)',
      surface3: 'rgba(253,186,116,0.14)',
      card: 'rgba(18, 21, 29, 0.70)',
      overlay: 'rgba(8, 9, 13, 0.86)',
    },
    borders: {
      subtle: 'rgba(255,250,246,0.08)',
      default: 'rgba(221,214,254,0.18)',
      glass: 'rgba(221,214,254,0.22)',
      accent: 'rgba(196,181,253,0.30)',
      active: 'rgba(196,181,253,0.42)',
    },
    accents: {
      primary: '#c4b5fd',
      secondary: '#d38f6c',
      soft: 'rgba(196,181,253,0.18)',
      softBorder: 'rgba(196,181,253,0.30)',
      info: '#bfdbfe',
      infoSoft: 'rgba(191,219,254,0.12)',
      infoBorder: 'rgba(191,219,254,0.24)',
      success: '#fde68a',
      successSoft: 'rgba(253,230,138,0.14)',
      successBorder: 'rgba(253,230,138,0.26)',
      warning: '#fdba74',
      warningSoft: 'rgba(253,186,116,0.14)',
      warningBorder: 'rgba(253,186,116,0.28)',
      danger: '#fda4af',
      dangerSoft: 'rgba(253,164,175,0.14)',
      dangerBorder: 'rgba(253,164,175,0.30)',
      neutralSoft: 'rgba(50, 57, 72, 0.66)',
      neutralBorder: 'rgba(221,214,254,0.24)',
    },
  },
  sage: {
    id: 'sage',
    labelKey: 'themeSage',
    descriptionKey: 'themeSageDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #171b17 0%, #5d7264 52%, #c6b690 100%)',
      accent: '#c7d7c2',
      accentAlt: '#e7d7b1',
    },
    palette: {
      primary: '#c7d7c2',
      primarySoft: '#ecf4e8',
      secondary: '#b89d72',
      secondarySoft: '#e7d7b1',
      backgroundStart: '#141814',
      backgroundMid: '#576c5d',
      backgroundEnd: '#8f8368',
      heroStart: '#0d110d',
      heroMid: '#364238',
      heroEnd: '#665c49',
      modalStart: '#171a17',
      modalEnd: '#0b0d0b',
      boardStart: '#1e231e',
      boardEnd: '#0e110e',
      textBody: '#fbfaf5',
      textMuted: '#dce6d5',
      textDisabled: '#aaa690',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.025)',
      surface1: 'rgba(28, 34, 29, 0.54)',
      surface2: 'rgba(199,215,194,0.12)',
      surface3: 'rgba(231,215,177,0.13)',
      card: 'rgba(16, 19, 16, 0.72)',
      overlay: 'rgba(7, 8, 7, 0.87)',
    },
    borders: {
      subtle: 'rgba(251,250,245,0.08)',
      default: 'rgba(199,215,194,0.18)',
      glass: 'rgba(199,215,194,0.22)',
      accent: 'rgba(199,215,194,0.28)',
      active: 'rgba(184,157,114,0.36)',
    },
    accents: {
      primary: '#c7d7c2',
      secondary: '#b89d72',
      soft: 'rgba(199,215,194,0.18)',
      softBorder: 'rgba(199,215,194,0.30)',
      info: '#d9ead3',
      infoSoft: 'rgba(217,234,211,0.12)',
      infoBorder: 'rgba(217,234,211,0.24)',
      success: '#dce6d5',
      successSoft: 'rgba(220,230,213,0.13)',
      successBorder: 'rgba(220,230,213,0.25)',
      warning: '#e7d7b1',
      warningSoft: 'rgba(231,215,177,0.14)',
      warningBorder: 'rgba(231,215,177,0.28)',
      danger: '#d8a7a7',
      dangerSoft: 'rgba(216,167,167,0.14)',
      dangerBorder: 'rgba(216,167,167,0.28)',
      neutralSoft: 'rgba(47, 56, 49, 0.68)',
      neutralBorder: 'rgba(199,215,194,0.24)',
    },
  },
  lilac: {
    id: 'lilac',
    labelKey: 'themeLilac',
    descriptionKey: 'themeLilacDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #1a1722 0%, #6d617d 52%, #8ea6c7 100%)',
      accent: '#d8b4fe',
      accentAlt: '#bfdbfe',
    },
    palette: {
      primary: '#d8b4fe',
      primarySoft: '#f3e8ff',
      secondary: '#7fa0c4',
      secondarySoft: '#dbeafe',
      backgroundStart: '#191621',
      backgroundMid: '#625870',
      backgroundEnd: '#748dad',
      heroStart: '#110f16',
      heroMid: '#3a3347',
      heroEnd: '#536882',
      modalStart: '#1a1721',
      modalEnd: '#0d0b11',
      boardStart: '#201c2a',
      boardEnd: '#0f0d14',
      textBody: '#fcf9ff',
      textMuted: '#eadcff',
      textDisabled: '#a39ab3',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(31, 27, 41, 0.56)',
      surface2: 'rgba(216,180,254,0.12)',
      surface3: 'rgba(191,219,254,0.13)',
      card: 'rgba(19, 16, 26, 0.72)',
      overlay: 'rgba(9, 7, 13, 0.87)',
    },
    borders: {
      subtle: 'rgba(252,249,255,0.08)',
      default: 'rgba(216,180,254,0.18)',
      glass: 'rgba(216,180,254,0.22)',
      accent: 'rgba(216,180,254,0.28)',
      active: 'rgba(191,219,254,0.36)',
    },
    accents: {
      primary: '#d8b4fe',
      secondary: '#7fa0c4',
      soft: 'rgba(216,180,254,0.18)',
      softBorder: 'rgba(216,180,254,0.30)',
      info: '#dbeafe',
      infoSoft: 'rgba(219,234,254,0.12)',
      infoBorder: 'rgba(219,234,254,0.24)',
      success: '#e9d5ff',
      successSoft: 'rgba(233,213,255,0.13)',
      successBorder: 'rgba(233,213,255,0.25)',
      warning: '#c4b5fd',
      warningSoft: 'rgba(196,181,253,0.14)',
      warningBorder: 'rgba(196,181,253,0.28)',
      danger: '#f9a8d4',
      dangerSoft: 'rgba(249,168,212,0.14)',
      dangerBorder: 'rgba(249,168,212,0.28)',
      neutralSoft: 'rgba(49, 43, 62, 0.68)',
      neutralBorder: 'rgba(216,180,254,0.24)',
    },
  },
  peach: {
    id: 'peach',
    labelKey: 'themePeach',
    descriptionKey: 'themePeachDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #1d1a1f 0%, #8d7b8c 52%, #d8b299 100%)',
      accent: '#f9c7b3',
      accentAlt: '#f2d4e8',
    },
    palette: {
      primary: '#f9c7b3',
      primarySoft: '#fde7de',
      secondary: '#d8a4c4',
      secondarySoft: '#f2d4e8',
      backgroundStart: '#1b171c',
      backgroundMid: '#7d6e7c',
      backgroundEnd: '#b88f7b',
      heroStart: '#120f13',
      heroMid: '#4a404a',
      heroEnd: '#876a59',
      modalStart: '#1b171b',
      modalEnd: '#0d0b0d',
      boardStart: '#221d22',
      boardEnd: '#100d10',
      textBody: '#fff9f6',
      textMuted: '#f6d9d0',
      textDisabled: '#b5a1a6',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(34, 28, 33, 0.56)',
      surface2: 'rgba(249,199,179,0.12)',
      surface3: 'rgba(242,212,232,0.13)',
      card: 'rgba(20, 16, 20, 0.72)',
      overlay: 'rgba(9, 7, 9, 0.87)',
    },
    borders: {
      subtle: 'rgba(255,249,246,0.08)',
      default: 'rgba(249,199,179,0.18)',
      glass: 'rgba(249,199,179,0.22)',
      accent: 'rgba(249,199,179,0.28)',
      active: 'rgba(216,164,196,0.36)',
    },
    accents: {
      primary: '#f9c7b3',
      secondary: '#d8a4c4',
      soft: 'rgba(249,199,179,0.18)',
      softBorder: 'rgba(249,199,179,0.30)',
      info: '#f2d4e8',
      infoSoft: 'rgba(242,212,232,0.12)',
      infoBorder: 'rgba(242,212,232,0.24)',
      success: '#fde7de',
      successSoft: 'rgba(253,231,222,0.13)',
      successBorder: 'rgba(253,231,222,0.25)',
      warning: '#fdba74',
      warningSoft: 'rgba(253,186,116,0.14)',
      warningBorder: 'rgba(253,186,116,0.28)',
      danger: '#f0a8a8',
      dangerSoft: 'rgba(240,168,168,0.14)',
      dangerBorder: 'rgba(240,168,168,0.28)',
      neutralSoft: 'rgba(54, 45, 52, 0.68)',
      neutralBorder: 'rgba(249,199,179,0.24)',
    },
  },
  seaglass: {
    id: 'seaglass',
    labelKey: 'themeSeaglass',
    descriptionKey: 'themeSeaglassDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #171c1f 0%, #6f8f95 52%, #d8b8ad 100%)',
      accent: '#b7efe2',
      accentAlt: '#f5c7da',
    },
    palette: {
      primary: '#b7efe2',
      primarySoft: '#e3faf5',
      secondary: '#f5c7da',
      secondarySoft: '#fde7f0',
      backgroundStart: '#161b1d',
      backgroundMid: '#68848a',
      backgroundEnd: '#bb998d',
      heroStart: '#101416',
      heroMid: '#41575c',
      heroEnd: '#8a7168',
      modalStart: '#181d1f',
      modalEnd: '#0b0d0e',
      boardStart: '#1d2528',
      boardEnd: '#0e1112',
      textBody: '#fcfffe',
      textMuted: '#d8f5ee',
      textDisabled: '#a6b4b2',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.03)',
      surface1: 'rgba(31, 39, 41, 0.56)',
      surface2: 'rgba(183,239,226,0.12)',
      surface3: 'rgba(245,199,218,0.13)',
      card: 'rgba(18, 23, 24, 0.72)',
      overlay: 'rgba(9, 10, 11, 0.87)',
    },
    borders: {
      subtle: 'rgba(252,255,254,0.08)',
      default: 'rgba(183,239,226,0.18)',
      glass: 'rgba(183,239,226,0.22)',
      accent: 'rgba(183,239,226,0.28)',
      active: 'rgba(245,199,218,0.36)',
    },
    accents: {
      primary: '#b7efe2',
      secondary: '#f5c7da',
      soft: 'rgba(183,239,226,0.18)',
      softBorder: 'rgba(183,239,226,0.30)',
      info: '#d9f4ff',
      infoSoft: 'rgba(217,244,255,0.12)',
      infoBorder: 'rgba(217,244,255,0.24)',
      success: '#d1fae5',
      successSoft: 'rgba(209,250,229,0.13)',
      successBorder: 'rgba(209,250,229,0.25)',
      warning: '#f9d8a7',
      warningSoft: 'rgba(249,216,167,0.14)',
      warningBorder: 'rgba(249,216,167,0.28)',
      danger: '#efb0b7',
      dangerSoft: 'rgba(239,176,183,0.14)',
      dangerBorder: 'rgba(239,176,183,0.28)',
      neutralSoft: 'rgba(49, 58, 60, 0.68)',
      neutralBorder: 'rgba(183,239,226,0.24)',
    },
  },
  moonstone: {
    id: 'moonstone',
    labelKey: 'themeMoonstone',
    descriptionKey: 'themeMoonstoneDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #13161c 0%, #586576 52%, #9aa7b5 100%)',
      accent: '#cbd5e1',
      accentAlt: '#93c5fd',
    },
    palette: {
      primary: '#cbd5e1',
      primarySoft: '#e2e8f0',
      secondary: '#93a8c2',
      secondarySoft: '#dbeafe',
      backgroundStart: '#12151a',
      backgroundMid: '#546170',
      backgroundEnd: '#7e8b99',
      heroStart: '#0c0e12',
      heroMid: '#313944',
      heroEnd: '#5a6674',
      modalStart: '#15181d',
      modalEnd: '#090b0e',
      boardStart: '#1a1f26',
      boardEnd: '#0c0f13',
      textBody: '#f8fbff',
      textMuted: '#d7e1eb',
      textDisabled: '#9ca7b3',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.025)',
      surface1: 'rgba(26, 31, 38, 0.58)',
      surface2: 'rgba(203,213,225,0.10)',
      surface3: 'rgba(147,197,253,0.10)',
      card: 'rgba(16, 19, 24, 0.74)',
      overlay: 'rgba(7, 8, 10, 0.88)',
    },
    borders: {
      subtle: 'rgba(248,251,255,0.08)',
      default: 'rgba(203,213,225,0.16)',
      glass: 'rgba(203,213,225,0.20)',
      accent: 'rgba(203,213,225,0.24)',
      active: 'rgba(147,197,253,0.30)',
    },
    accents: {
      primary: '#cbd5e1',
      secondary: '#93a8c2',
      soft: 'rgba(203,213,225,0.14)',
      softBorder: 'rgba(203,213,225,0.24)',
      info: '#dbeafe',
      infoSoft: 'rgba(219,234,254,0.10)',
      infoBorder: 'rgba(219,234,254,0.20)',
      success: '#e2e8f0',
      successSoft: 'rgba(226,232,240,0.12)',
      successBorder: 'rgba(226,232,240,0.22)',
      warning: '#cbd5e1',
      warningSoft: 'rgba(203,213,225,0.12)',
      warningBorder: 'rgba(203,213,225,0.22)',
      danger: '#94a3b8',
      dangerSoft: 'rgba(148,163,184,0.14)',
      dangerBorder: 'rgba(148,163,184,0.24)',
      neutralSoft: 'rgba(38, 45, 54, 0.72)',
      neutralBorder: 'rgba(203,213,225,0.22)',
    },
  },
  sandstone: {
    id: 'sandstone',
    labelKey: 'themeSandstone',
    descriptionKey: 'themeSandstoneDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #181613 0%, #6d655d 52%, #b6aa97 100%)',
      accent: '#d6c7b3',
      accentAlt: '#f2ddb6',
    },
    palette: {
      primary: '#d6c7b3',
      primarySoft: '#efe6da',
      secondary: '#c6a97d',
      secondarySoft: '#f2ddb6',
      backgroundStart: '#161411',
      backgroundMid: '#645d55',
      backgroundEnd: '#938776',
      heroStart: '#0e0d0b',
      heroMid: '#3e3933',
      heroEnd: '#6d6458',
      modalStart: '#171512',
      modalEnd: '#0a0908',
      boardStart: '#1d1a17',
      boardEnd: '#0d0c0a',
      textBody: '#fffdf9',
      textMuted: '#e6dccf',
      textDisabled: '#a9a08f',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.025)',
      surface1: 'rgba(29, 26, 23, 0.58)',
      surface2: 'rgba(214,199,179,0.10)',
      surface3: 'rgba(242,221,182,0.11)',
      card: 'rgba(18, 16, 13, 0.75)',
      overlay: 'rgba(8, 7, 5, 0.88)',
    },
    borders: {
      subtle: 'rgba(255,253,249,0.08)',
      default: 'rgba(214,199,179,0.16)',
      glass: 'rgba(214,199,179,0.20)',
      accent: 'rgba(214,199,179,0.24)',
      active: 'rgba(198,169,125,0.30)',
    },
    accents: {
      primary: '#d6c7b3',
      secondary: '#c6a97d',
      soft: 'rgba(214,199,179,0.14)',
      softBorder: 'rgba(214,199,179,0.24)',
      info: '#efe6da',
      infoSoft: 'rgba(239,230,218,0.10)',
      infoBorder: 'rgba(239,230,218,0.20)',
      success: '#f2ddb6',
      successSoft: 'rgba(242,221,182,0.12)',
      successBorder: 'rgba(242,221,182,0.22)',
      warning: '#d6c7b3',
      warningSoft: 'rgba(214,199,179,0.12)',
      warningBorder: 'rgba(214,199,179,0.22)',
      danger: '#bfa58f',
      dangerSoft: 'rgba(191,165,143,0.14)',
      dangerBorder: 'rgba(191,165,143,0.24)',
      neutralSoft: 'rgba(44, 40, 35, 0.74)',
      neutralBorder: 'rgba(214,199,179,0.22)',
    },
  },
  truffle: {
    id: 'truffle',
    labelKey: 'themeTruffle',
    descriptionKey: 'themeTruffleDesc',
    preview: {
      gradient: 'linear-gradient(135deg, #11100d 0%, #3f473e 52%, #7e8f78 100%)',
      accent: '#c8d1bf',
      accentAlt: '#9db0a0',
    },
    palette: {
      primary: '#c8d1bf',
      primarySoft: '#e6ece0',
      secondary: '#9db0a0',
      secondarySoft: '#d6e1d4',
      backgroundStart: '#11100d',
      backgroundMid: '#3d443d',
      backgroundEnd: '#667260',
      heroStart: '#0a0907',
      heroMid: '#272c26',
      heroEnd: '#4a5547',
      modalStart: '#13120f',
      modalEnd: '#070705',
      boardStart: '#181713',
      boardEnd: '#0b0b08',
      textBody: '#fafcf7',
      textMuted: '#dbe4d4',
      textDisabled: '#9ea79a',
      white: '#ffffff',
      black: '#000000',
    },
    surfaces: {
      surface0: 'rgba(255,255,255,0.025)',
      surface1: 'rgba(26, 29, 24, 0.58)',
      surface2: 'rgba(200,209,191,0.10)',
      surface3: 'rgba(157,176,160,0.11)',
      card: 'rgba(16, 18, 14, 0.75)',
      overlay: 'rgba(8, 7, 5, 0.88)',
    },
    borders: {
      subtle: 'rgba(250,252,247,0.08)',
      default: 'rgba(200,209,191,0.16)',
      glass: 'rgba(200,209,191,0.20)',
      accent: 'rgba(200,209,191,0.24)',
      active: 'rgba(157,176,160,0.30)',
    },
    accents: {
      primary: '#c8d1bf',
      secondary: '#9db0a0',
      soft: 'rgba(200,209,191,0.14)',
      softBorder: 'rgba(200,209,191,0.24)',
      info: '#d6e1d4',
      infoSoft: 'rgba(214,225,212,0.10)',
      infoBorder: 'rgba(214,225,212,0.20)',
      success: '#c8d6bf',
      successSoft: 'rgba(200,214,191,0.12)',
      successBorder: 'rgba(200,214,191,0.22)',
      warning: '#b9b08d',
      warningSoft: 'rgba(185,176,141,0.12)',
      warningBorder: 'rgba(185,176,141,0.22)',
      danger: '#b78f86',
      dangerSoft: 'rgba(183,143,134,0.14)',
      dangerBorder: 'rgba(183,143,134,0.24)',
      neutralSoft: 'rgba(34, 37, 31, 0.72)',
      neutralBorder: 'rgba(200,209,191,0.22)',
    },
  },
};

const buildTheme = (theme) => {
  const gradients = {
    appBackground: `linear-gradient(135deg, ${theme.palette.backgroundStart} 0%, ${theme.palette.backgroundMid} 52%, ${theme.palette.backgroundEnd} 100%)`,
    heroBackground: `linear-gradient(135deg, ${theme.palette.heroStart} 0%, ${theme.palette.heroMid} 52%, ${theme.palette.heroEnd} 100%)`,
    modal: `linear-gradient(135deg, ${theme.palette.modalStart}, ${theme.palette.modalEnd})`,
    buttonPrimary: `linear-gradient(135deg, ${theme.palette.heroMid} 0%, ${theme.palette.secondary} 100%)`,
    buttonGreen: `linear-gradient(135deg, ${theme.palette.heroMid} 0%, ${theme.accents.success} 100%)`,
    buttonRed: `linear-gradient(135deg, ${theme.palette.modalStart} 0%, ${theme.accents.danger} 100%)`,
    boardBackground: `linear-gradient(145deg, ${theme.palette.boardStart}, ${theme.palette.boardEnd})`,
  };

  const text = {
    body: theme.palette.textBody,
    muted: theme.palette.textMuted,
    disabled: theme.palette.textDisabled,
  };

  const TOKENS = {
    surface0: theme.surfaces.surface0,
    surface1: theme.surfaces.surface1,
    surface2: theme.surfaces.surface2,
    surface3: theme.surfaces.surface3,
    borderSubtle: theme.borders.subtle,
    borderDefault: theme.borders.default,
    borderAccent: theme.borders.accent,
    borderActive: theme.borders.active,
    textBody: text.body,
    textMuted: text.muted,
    textDisabled: text.disabled,
  };

  return {
    ...theme,
    text,
    gradients,
    shadows: sharedShadows,
    radii,
    space,
    zIndex,
    typography,
    TOKENS,
  };
};

const themes = Object.fromEntries(
  Object.entries(themeLibrary).map(([key, value]) => [key, buildTheme(value)])
);

const themeGroupById = {
  rose: 'pastel',
  dawn: 'pastel',
  sage: 'pastel',
  lilac: 'pastel',
  peach: 'pastel',
  seaglass: 'pastel',
  nebula: 'vivid',
  ember: 'vivid',
  aurora: 'vivid',
  sunset: 'vivid',
  citrus: 'vivid',
  laser: 'vivid',
  lagoon: 'neutral',
  forest: 'neutral',
  graphite: 'neutral',
  moonstone: 'neutral',
  sandstone: 'neutral',
  truffle: 'neutral',
};

export const THEME_OPTIONS = Object.values(themeLibrary).map(({ id, labelKey, descriptionKey, preview }) => ({
  key: id,
  labelKey,
  descriptionKey,
  preview,
  group: themeGroupById[id] || 'vivid',
}));

export const isThemeKey = (themeKey) => Object.prototype.hasOwnProperty.call(themes, themeKey);

export const normalizeThemeKey = (themeKey) => (isThemeKey(themeKey) ? themeKey : DEFAULT_THEME_KEY);

export const getTheme = (themeKey = DEFAULT_THEME_KEY) => themes[normalizeThemeKey(themeKey)];

export const getThemeCssVars = (themeKey = DEFAULT_THEME_KEY) => {
  const theme = getTheme(themeKey);
  return {
    '--theme-accent': theme.accents.primary,
    '--theme-accent-strong': theme.accents.secondary,
    '--theme-accent-soft': theme.accents.soft,
    '--theme-accent-soft-border': theme.accents.softBorder,
    '--theme-info': theme.accents.info,
    '--theme-info-soft': theme.accents.infoSoft,
    '--theme-info-border': theme.accents.infoBorder,
    '--theme-success': theme.accents.success,
    '--theme-success-soft': theme.accents.successSoft,
    '--theme-success-border': theme.accents.successBorder,
    '--theme-warning': theme.accents.warning,
    '--theme-warning-soft': theme.accents.warningSoft,
    '--theme-warning-border': theme.accents.warningBorder,
    '--theme-danger': theme.accents.danger,
    '--theme-danger-soft': theme.accents.dangerSoft,
    '--theme-danger-border': theme.accents.dangerBorder,
    '--theme-neutral-soft': theme.accents.neutralSoft,
    '--theme-neutral-border': theme.accents.neutralBorder,
    '--theme-text-body': theme.text.body,
    '--theme-text-muted': theme.text.muted,
    '--theme-text-disabled': theme.text.disabled,
    '--theme-surface-muted': theme.surfaces.surface1,
    '--theme-surface-strong': theme.surfaces.surface2,
    '--theme-surface-emphasis': theme.surfaces.surface3,
    '--theme-border-subtle': theme.borders.subtle,
    '--theme-border-default': theme.borders.default,
    '--theme-border-accent': theme.borders.active,
    '--theme-app-background': theme.gradients.appBackground,
    '--theme-hero-background': theme.gradients.heroBackground,
    '--theme-modal-background': theme.gradients.modal,
    '--theme-board-background': theme.gradients.boardBackground,
    '--theme-button-primary': theme.gradients.buttonPrimary,
    '--theme-button-green': theme.gradients.buttonGreen,
    '--theme-button-danger': theme.gradients.buttonRed,
  };
};

export const palette = getTheme(DEFAULT_THEME_KEY).palette;
export const surfaces = getTheme(DEFAULT_THEME_KEY).surfaces;
export const borders = getTheme(DEFAULT_THEME_KEY).borders;
export const text = getTheme(DEFAULT_THEME_KEY).text;
export const gradients = getTheme(DEFAULT_THEME_KEY).gradients;
export const shadows = sharedShadows;
export const TOKENS = getTheme(DEFAULT_THEME_KEY).TOKENS;

const theme = getTheme(DEFAULT_THEME_KEY);

export default theme;
